import { divide, reduceWhile } from 'helpers/ramda';
import { Product } from 'types/IProducts';

/**
 * Impact price: The average price per contract that can be achieved with the given premium.
 * Total contracts: The total number of contracts that can be purchased with the given premium.
 * @param premium - The premium amount.
 * @param orderbook - The current order book.
 * @param product - The product details.
 * @returns - The average price (impact price) and the total number of contracts that can be purchased with a given premium based on the current order book.
 */
const getImpactFromPremium = (premium: number, orderbook: any, product: Product) => {
  try {
    if (!orderbook) {
      throw new Error('Orderbook is required');
    }
    // Accumulate the total cost and size of contracts
    const iterator = (acc: any, book: any) => {
      return {
        avgNumerator:
          acc.avgNumerator +
          Number(book.price) * Number(book.size) * Number(product.contract_value),
        avgDenominator:
          acc.avgDenominator + Number(book.size) * Number(product.contract_value),
      };
    };
    const predicate = (acc: { avgNumerator: number }) => {
      return Number(acc.avgNumerator) <= Number(premium);
    };

    const impact = reduceWhile(
      predicate,
      iterator,
      {
        avgNumerator: 0,
        avgDenominator: 0,
      },
      orderbook
    );

    const pricePerUnderlying = divide(impact.avgNumerator, impact.avgDenominator);
    const pricePerContract = Number(pricePerUnderlying) * Number(product.contract_value);
    const totalContracts = premium / pricePerContract;

    return {
      impactPrice: pricePerUnderlying,
      totalContracts: parseInt(String(totalContracts), 10),
    };
  } catch (error) {
    console.error('Error in getImpactFromPremium:', error);
    return {
      impactPrice: 0,
      totalContracts: 0,
    };
  }
};

export { getImpactFromPremium };
